// ドロップ率 λ の推定（§5.2 / §12.2.3）。RateMode を解決済みレート＋表示用メタに変換する。
import { ROUGH_K_THRESHOLD, STRENGTH_M } from '../config';
import { gammaCI90 } from './gamma-fn';
import type { RateMode, ResolvedRate } from './types';

export interface ResolvedRateInfo {
  /** エンジンが消費する解決済みレート（fixed / gamma）。 */
  resolved: ResolvedRate;
  /** 点推定 λ̂（期待個数欄の自動充填・表示に使う）。 */
  lambdaHat: number;
  /** 90% 信用区間 [5%,95%]（direct は null）。 */
  ci90: [number, number] | null;
  /** 観測個数 k が小さく推定が粗いか（k<ROUGH_K_THRESHOLD, §5.2）。 */
  rough: boolean;
  /** 観測個数 k（direct は null）。 */
  observedK: number | null;
}

export function resolveRate(mode: RateMode): ResolvedRateInfo {
  switch (mode.kind) {
    case 'direct': {
      const lambda = Math.max(0, mode.lambda);
      return {
        resolved: { kind: 'fixed', lambda },
        lambdaHat: lambda,
        ci90: null,
        rough: false,
        observedK: null,
      };
    }
    case 'empirical': {
      // Jeffreys 事前 → 事後 Gamma(k+0.5, N)。k=0 でも λ̂>0（§5.2 不変条件）。
      const N = mode.N;
      const k = Math.max(0, mode.k);
      if (N <= 0) return degenerate(k);
      return makeGamma(k + 0.5, N, k);
    }
    case 'empirical_prior': {
      // 予測 λ₀・信頼度 M を実績にベイズ合成 → 事後 Gamma(λ₀M+k, M+N)。
      const M = STRENGTH_M[mode.strength];
      const N = Math.max(0, mode.N);
      const k = Math.max(0, mode.k);
      const priorRate = Math.max(0, mode.priorRate);
      return makeGamma(priorRate * M + k, M + N, k);
    }
  }
}

function makeGamma(alpha: number, beta: number, k: number): ResolvedRateInfo {
  const lambdaHat = alpha / beta;
  return {
    resolved: { kind: 'gamma', alpha, beta, lambdaHat },
    lambdaHat,
    ci90: gammaCI90(alpha, beta),
    rough: k < ROUGH_K_THRESHOLD,
    observedK: k,
  };
}

// 実績モードで N≤0（推定不能）の安全フォールバック。
function degenerate(k: number): ResolvedRateInfo {
  return {
    resolved: { kind: 'fixed', lambda: 0 },
    lambdaHat: 0,
    ci90: null,
    rough: true,
    observedK: k,
  };
}
